import React, { useState } from 'react';
import { COLORS } from '../utils/constants';

const PIN_LENGTH = 6;

/**
 * Numeric PIN keypad with masked entry dots.
 *
 * Props:
 *   title      - Label shown above the dots
 *   onComplete - Called with the completed PIN string
 *   onCancel   - Optional callback for a "Cancelar" link
 */
const PinKeypad = ({ title = 'Digite seu PIN', onComplete, onCancel }) => {
  const [pin, setPin] = useState('');

  const handleDigit = (digit) => {
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === PIN_LENGTH) {
      // Brief delay so the last dot fills before the callback fires
      setTimeout(() => {
        onComplete(next);
        setPin('');
      }, 150);
    }
  };

  const handleDelete = () => setPin((p) => p.slice(0, -1));

  // Keys: 3×4 grid — blank placeholder in bottom-left, 0 in bottom-center, ⌫ in bottom-right
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

  return (
    <div style={s.container}>
      <p style={s.title}>{title}</p>

      {/* PIN indicator dots */}
      <div style={s.dotsRow}>
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            style={i < pin.length ? { ...s.dot, ...s.dotFilled } : s.dot}
          />
        ))}
      </div>

      {/* Numeric keypad */}
      <div style={s.keypad}>
        {keys.map((key, i) => (
          <button
            key={i}
            style={key === '' ? { ...s.key, ...s.keyInvisible } : s.key}
            disabled={key === ''}
            onClick={() => {
              if (key === '⌫') handleDelete();
              else if (key !== '') handleDigit(key);
            }}
          >
            {key}
          </button>
        ))}
      </div>

      {onCancel && (
        <button style={s.cancelLink} onClick={onCancel}>
          Cancelar
        </button>
      )}
    </div>
  );
};

const s = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    padding: '8px 0',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.text,
    margin: 0,
    textAlign: 'center',
  },
  dotsRow: {
    display: 'flex',
    gap: 14,
  },
  dot: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: `2px solid ${COLORS.primary}`,
    backgroundColor: 'transparent',
    transition: 'background-color 0.1s',
  },
  dotFilled: {
    backgroundColor: COLORS.primary,
  },
  keypad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 72px)',
    gap: 10,
  },
  key: {
    height: 60,
    fontSize: 22,
    fontWeight: '600',
    color: COLORS.text,
    backgroundColor: COLORS.backgroundSecondary,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyInvisible: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'default',
    pointerEvents: 'none',
  },
  cancelLink: {
    background: 'none',
    border: 'none',
    color: COLORS.textSecondary,
    fontSize: 14,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    marginTop: 4,
  },
};

export default PinKeypad;
